import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  Shield, Star, Check, ArrowUpRight, ArrowDownRight, Loader2,
  CreditCard, Calendar, Receipt, ChevronDown, ChevronUp, Zap, HardDrive, Folder
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionManager({ isDarkMode }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [subStatus, setSubStatus] = useState('trial');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changingPlan, setChangingPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showHistory, setShowHistory] = useState(false);

  const bgCard = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-white';
  const bgInner = isDarkMode ? 'bg-[#252540]' : 'bg-gray-100';
  const border = isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200';
  const text = isDarkMode ? 'text-white' : 'text-gray-900';
  const textMuted = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [plansRes, historyRes] = await Promise.all([
        axios.get(`${API}/billing/available-plans`),
        axios.get(`${API}/billing/payment-history`),
      ]);
      setPlans(plansRes.data.plans.filter(p => !p.is_hidden));
      setCurrentPlanId(plansRes.data.current_plan_id);
      setSubStatus(plansRes.data.subscription_status);
      setTransactions(historyRes.data.transactions);
    } catch (err) {
      console.error('Failed to load subscription data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlan = async (planId) => {
    setChangingPlan(planId);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        plan_id: planId,
        billing_cycle: billingCycle,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setChangingPlan(null);
    }
  };

  const currentPlan = plans.find(p => p.is_current);

  const getPlanRelation = (plan) => {
    if (!currentPlan) return 'upgrade';
    if (plan.id === currentPlan.id) return 'current';
    if (plan.monthly_price > currentPlan.monthly_price) return 'upgrade';
    return 'downgrade';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Plan Summary */}
      <Card className={`${bgCard} ${border}`}>
        <CardHeader className="pb-3">
          <CardTitle className={`${text} flex items-center gap-2`}>
            <Shield className="w-5 h-5 text-[#6D4AFF]" />
            Current Subscription
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`flex items-center justify-between p-4 ${bgInner} rounded-lg`}>
            <div>
              <div className="flex items-center gap-2">
                <p className={`${text} font-semibold text-lg`}>
                  {currentPlan?.name || user?.subscription_plan_name || 'Free Trial'}
                </p>
                {subStatus === 'active' && (
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Active</Badge>
                )}
                {subStatus === 'trial' && (
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Trial</Badge>
                )}
                {subStatus === 'expired' && (
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Expired</Badge>
                )}
              </div>
              {currentPlan && (
                <div className="flex items-center gap-4 mt-2 text-sm">
                  <span className={textMuted}>
                    ${billingCycle === 'yearly' ? currentPlan.yearly_price : currentPlan.monthly_price}/{billingCycle === 'yearly' ? 'yr' : 'mo'}
                  </span>
                  {currentPlan.storage_limit && (
                    <span className={`${textMuted} flex items-center gap-1`}>
                      <HardDrive className="w-3 h-3" /> {currentPlan.storage_limit}
                    </span>
                  )}
                  {currentPlan.wall_storage_gb > 0 && (
                    <span className={`${textMuted} flex items-center gap-1`}>
                      <Zap className="w-3 h-3" /> {currentPlan.wall_storage_gb}GB Wall
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billing Cycle Toggle */}
      <div className="flex items-center justify-center gap-3">
        <button
          onClick={() => setBillingCycle('monthly')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            billingCycle === 'monthly'
              ? 'bg-[#6D4AFF] text-white'
              : `${bgInner} ${textMuted} hover:text-white`
          }`}
          data-testid="billing-monthly-toggle"
        >
          Monthly
        </button>
        <button
          onClick={() => setBillingCycle('yearly')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
            billingCycle === 'yearly'
              ? 'bg-[#6D4AFF] text-white'
              : `${bgInner} ${textMuted} hover:text-white`
          }`}
          data-testid="billing-yearly-toggle"
        >
          Yearly
          <Badge className="bg-emerald-500/20 text-emerald-400 text-xs py-0">Save 20%</Badge>
        </button>
      </div>

      {/* Available Plans */}
      <Card className={`${bgCard} ${border}`}>
        <CardHeader className="pb-3">
          <CardTitle className={`${text} text-sm`}>Change Plan</CardTitle>
          <CardDescription className={textMuted}>
            Select a plan to upgrade or downgrade your subscription
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map((plan) => {
              const relation = getPlanRelation(plan);
              const price = billingCycle === 'yearly' ? plan.yearly_price : plan.monthly_price;
              const isCurrent = relation === 'current';

              return (
                <div
                  key={plan.id}
                  data-testid={`plan-card-${plan.id}`}
                  className={`relative rounded-xl border p-4 transition-all ${
                    isCurrent
                      ? 'border-[#6D4AFF] bg-[#6D4AFF]/5 ring-1 ring-[#6D4AFF]/30'
                      : `${border} ${bgInner} hover:border-[#6D4AFF]/40`
                  }`}
                >
                  {plan.is_featured && !isCurrent && (
                    <Badge className="absolute -top-2 right-3 bg-amber-500 text-black text-xs">Popular</Badge>
                  )}
                  {isCurrent && (
                    <Badge className="absolute -top-2 right-3 bg-[#6D4AFF] text-white text-xs">Current</Badge>
                  )}

                  <h3 className={`font-semibold ${text}`}>{plan.name}</h3>

                  <div className="mt-2">
                    <span className={`text-2xl font-bold ${text}`}>${price}</span>
                    <span className={`text-sm ${textMuted}`}>/{billingCycle === 'yearly' ? 'yr' : 'mo'}</span>
                  </div>

                  {plan.description && (
                    <p className={`text-xs ${textMuted} mt-1 line-clamp-2`}>{plan.description}</p>
                  )}

                  <div className={`mt-3 space-y-1.5 text-xs ${textMuted}`}>
                    {plan.storage_limit && (
                      <div className="flex items-center gap-1.5">
                        <HardDrive className="w-3 h-3" /> {plan.storage_limit} storage
                      </div>
                    )}
                    {plan.custom_folders_limit > 0 && (
                      <div className="flex items-center gap-1.5">
                        <Folder className="w-3 h-3" /> {plan.custom_folders_limit} custom folders
                      </div>
                    )}
                    {plan.wall_storage_gb > 0 && (
                      <div className="flex items-center gap-1.5">
                        <Zap className="w-3 h-3 text-[#6D4AFF]" /> {plan.wall_storage_gb}GB Wall storage
                      </div>
                    )}
                  </div>

                  <div className="mt-4">
                    {isCurrent ? (
                      <Button disabled className="w-full bg-[#6D4AFF]/20 text-[#6D4AFF]" size="sm">
                        <Check className="w-4 h-4 mr-1" /> Current Plan
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleChangePlan(plan.id)}
                        disabled={changingPlan === plan.id}
                        size="sm"
                        className={`w-full ${
                          relation === 'upgrade'
                            ? 'bg-[#6D4AFF] hover:bg-[#5B3FD9] text-white'
                            : 'bg-gray-600 hover:bg-gray-500 text-white'
                        }`}
                        data-testid={`change-plan-${plan.id}`}
                      >
                        {changingPlan === plan.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : relation === 'upgrade' ? (
                          <><ArrowUpRight className="w-4 h-4 mr-1" /> Upgrade</>
                        ) : (
                          <><ArrowDownRight className="w-4 h-4 mr-1" /> Downgrade</>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card className={`${bgCard} ${border}`}>
        <CardHeader className="pb-3">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center justify-between w-full"
            data-testid="toggle-payment-history"
          >
            <CardTitle className={`${text} text-sm flex items-center gap-2`}>
              <Receipt className="w-4 h-4 text-[#6D4AFF]" />
              Payment History
              {transactions.length > 0 && (
                <Badge className="bg-[#6D4AFF]/20 text-[#6D4AFF] text-xs">{transactions.length}</Badge>
              )}
            </CardTitle>
            {showHistory ? <ChevronUp className={`w-4 h-4 ${textMuted}`} /> : <ChevronDown className={`w-4 h-4 ${textMuted}`} />}
          </button>
        </CardHeader>
        {showHistory && (
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-8">
                <Receipt className={`w-10 h-10 mx-auto mb-3 ${textMuted} opacity-40`} />
                <p className={textMuted}>No payment history yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {transactions.map((tx, i) => (
                  <div key={tx.id || i} className={`flex items-center justify-between p-3 ${bgInner} rounded-lg`} data-testid={`transaction-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        tx.payment_status === 'paid' ? 'bg-emerald-500/20' : 'bg-amber-500/20'
                      }`}>
                        <CreditCard className={`w-4 h-4 ${
                          tx.payment_status === 'paid' ? 'text-emerald-400' : 'text-amber-400'
                        }`} />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${text}`}>{tx.plan_name}</p>
                        <p className={`text-xs ${textMuted}`}>
                          {formatDate(tx.created_at)} {tx.billing_cycle && `· ${tx.billing_cycle}`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-medium ${text}`}>
                        ${tx.amount.toFixed(2)}
                      </p>
                      <Badge className={`text-xs ${
                        tx.payment_status === 'paid'
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : tx.payment_status === 'pending'
                          ? 'bg-amber-500/20 text-amber-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {tx.payment_status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
